import React from 'react';
import Icon from '../../../components/AppIcon';
import departmentalBudgetService from '../../../services/departmentalBudgetService';

const ForecastingPanel = ({ forecasts }) => {
  if (!forecasts || forecasts?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-center">
        <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
        <p className="text-muted-foreground">No forecast data available</p>
      </div>
    );
  }

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-success';
    if (score >= 0.6) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="space-y-4">
      {forecasts?.map((forecast) => (
        <div key={forecast?.id} className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground">
                {forecast?.forecastPeriod === 'quarterly' ? 'Quarterly' : 'Annual'} Forecast
              </h3>
              <p className="text-sm text-muted-foreground">
                {forecast?.forecastYear} {forecast?.forecastQuarter ? `Q${forecast?.forecastQuarter}` : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Confidence Score</p>
              <p className={`text-xl font-bold ${getConfidenceColor(forecast?.confidenceScore)}`}>
                {(forecast?.confidenceScore * 100)?.toFixed(0)}%
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-primary/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={18} color="var(--color-primary)" />
                <span className="text-sm text-muted-foreground">Projected Budget</span>
              </div>
              <p className="text-2xl font-heading font-bold text-foreground">
                {departmentalBudgetService?.formatKina(forecast?.projectedBudgetKina)}
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Users" size={18} color="var(--color-primary)" />
                <span className="text-sm text-muted-foreground">Headcount Projection</span>
              </div>
              <p className="text-2xl font-heading font-bold text-foreground">
                {forecast?.headcountProjection} employees
              </p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Projected Payroll</span>
              <span className="font-medium text-foreground">
                {departmentalBudgetService?.formatKina(forecast?.projectedPayrollKina)}
              </span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Projected Compliance Costs</span>
              <span className="font-medium text-foreground">
                {departmentalBudgetService?.formatKina(forecast?.projectedComplianceKina)}
              </span>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">Projected Operational Costs</span>
              <span className="font-medium text-foreground">
                {departmentalBudgetService?.formatKina(forecast?.projectedOperationalKina)}
              </span>
            </div>
          </div>

          {forecast?.assumptions && (
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-foreground mb-1">Assumptions</p>
                  <p className="text-xs text-muted-foreground">
                    {forecast?.assumptions?.assumption || 'Based on historical spending patterns'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ForecastingPanel;